// Utility functions for ID Card and Visiting Card components

/**
 * Generate QR code data URL for the given user
 * @param {string} userEmail - User email without domain
 * @param {string} type - 'idcard' or 'vc'
 * @param {string} baseUrl - Base URL of the application
 * @returns {string} QR code data URL
 */
export const generateQRCodeData = (userEmail, type = 'idcard', baseUrl = window.location.origin) => {
  // Check if userEmail is valid
  if (!userEmail || typeof userEmail !== 'string') {
    console.warn('generateQRCodeData: userEmail is invalid:', userEmail);
    return `${baseUrl}/${type}/unknown`;
  }
  
  const cleanEmail = userEmail.replace('@ba-systems.com', '');
  return `${baseUrl}/${type}/${cleanEmail}`;
};

/**
 * Download image as file
 * @param {string} dataUrl - Data URL of the image
 * @param {string} filename - Name of the file to download
 */
export const downloadImage = (dataUrl, filename) => {
  const link = document.createElement('a');
  link.download = filename;
  link.href = dataUrl;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

/**
 * Generate vCard content for contact download
 * @param {Object} userData - User data object
 * @returns {string} vCard formatted string
 */
export const generateVCard = (userData) => {
  return `BEGIN:VCARD
VERSION:3.0
FN:${userData.user_name || ''}
ORG:Business Automation Ltd.
TITLE:${userData.designation || ''}
TEL:${userData.user_phone || ''}
EMAIL:${userData.user_email || ''}
ADR:;;${userData.office_address || ''};;;;
URL:www.ba-systems.com
END:VCARD`;
};

/**
 * Download vCard file
 * @param {Object} userData - User data object
 */
export const downloadVCard = (userData) => {
  const vcard = generateVCard(userData);
  const blob = new Blob([vcard], { type: 'text/vcard' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${userData.user_name?.replace(/\s+/g, '_') || 'contact'}.vcf`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
};

/**
 * Validate user data structure
 * @param {Object} userData - User data to validate
 * @param {string} type - 'idcard' or 'vc'
 * @returns {Object} Validation result with isValid and errors
 */
export const validateUserData = (userData, type = 'idcard') => {
  const errors = [];
  
  if (!userData) {
    return { isValid: false, errors: ['User data is required'] };
  }

  // Common required fields
  const requiredFields = ['user_name', 'user_email'];
  
  // Type-specific required fields
  if (type === 'idcard') {
    requiredFields.push('employee_id', 'designation', 'user_phone');
  } else if (type === 'vc') {
    requiredFields.push('designation', 'user_phone');
  }

  requiredFields.forEach(field => {
    if (!userData[field] || userData[field].trim() === '') {
      errors.push(`${field.replace('_', ' ')} is required`);
    }
  });

  // Email validation
  if (userData.user_email && !isValidEmail(userData.user_email)) {
    errors.push('Invalid email format');
  }

  // Phone validation
  if (userData.user_phone && !isValidPhone(userData.user_phone)) {
    errors.push('Invalid phone format');
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Validate email format
 * @param {string} email - Email to validate
 * @returns {boolean} Is valid email
 */
export const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Validate phone format
 * @param {string} phone - Phone to validate
 * @returns {boolean} Is valid phone
 */
export const isValidPhone = (phone) => {
  const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/;
  return phoneRegex.test(phone.replace(/[\s\-\(\)]/g, ''));
};

/**
 * Format user data with defaults
 * @param {Object} userData - Raw user data
 * @param {string} type - 'idcard' or 'vc'
 * @returns {Object} Formatted user data
 */
export const formatUserData = (userData, type = 'idcard') => {
  const defaults = {
    user_name: '',
    designation: '',
    user_phone: '',
    user_email: '',
    user_pic: null,
    office_address: 'Dhaka, Bangladesh',
    ...(type === 'idcard' && {
      employee_id: '',
      blood_grp: '',
      user_emergency_contact: '',
      office_contact: '+880-9602-666777'
    })
  };

  return { ...defaults, ...userData };
};

/**
 * Get user profile image URL
 * @param {string} userPic - User picture filename
 * @param {string} defaultImage - Default image path
 * @returns {string} Image URL
 */
export const getUserImageUrl = (userPic, defaultImage = '/assets/images/default_profile.jpg') => {
  if (userPic) {
    return `https://ebs.ba-systems.com/users/upload/${userPic}`;
  }
  return defaultImage;
};

/**
 * Convert canvas to PDF and download
 * @param {HTMLCanvasElement} canvas - Canvas element
 * @param {string} filename - PDF filename
 * @param {Object} options - PDF options
 */
export const downloadCanvasAsPDF = async (canvas, filename, options = {}) => {
  const { jsPDF } = window.jspdf || await import('jspdf');
  
  const {
    orientation = 'portrait',
    format = 'a4',
    unit = 'mm',
    quality = 1.0
  } = options;

  const pdf = new jsPDF({ orientation, unit, format });
  const imgData = canvas.toDataURL('image/png', quality);
  
  // Calculate dimensions to fit the PDF
  const pdfWidth = pdf.internal.pageSize.getWidth();
  const pdfHeight = pdf.internal.pageSize.getHeight();
  const canvasAspectRatio = canvas.height / canvas.width;
  
  let imgWidth = pdfWidth - 20; // 10mm margin on each side
  let imgHeight = imgWidth * canvasAspectRatio;
  
  // If height exceeds page height, scale down
  if (imgHeight > pdfHeight - 20) {
    imgHeight = pdfHeight - 20;
    imgWidth = imgHeight / canvasAspectRatio;
  }
  
  const x = (pdfWidth - imgWidth) / 2;
  const y = (pdfHeight - imgHeight) / 2;
  
  pdf.addImage(imgData, 'PNG', x, y, imgWidth, imgHeight);
  pdf.save(filename);
};